import React, {useState, useEffect} from 'react';
import Modal from './Modal';
import axiosInstance from "../utility/axiosInstance";
import {Instance} from '../store/slices/instancesSlice';
import {useToast} from "./ToastContext";
import Select from 'react-select';
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../store/store";
import {fetchSecurityGroups, SecurityGroup} from "../store/slices/securityGroupsSlice";

interface SecurityGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    instance: Instance | null;
    onUpdated: () => void;
}


const SecurityGroupModal: React.FC<SecurityGroupModalProps> = ({isOpen, onClose, instance, onUpdated}) => {
    const {showToast} = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const availableGroups = useSelector((state: RootState) => state.securityGroups.data);
    const securityGroupsStatus = useSelector((state: RootState) => state.securityGroups.status);

    const [saving, setSaving] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        if (isOpen && instance) {
            if (availableGroups.length === 0 && securityGroupsStatus === 'idle') {
                dispatch(fetchSecurityGroups());
            }
            const currentGroups = instance.security_groups?.map(name => ({value: name, label: name})) || [];
            setSelectedGroups(currentGroups);
        }
    }, [isOpen, instance, availableGroups.length, securityGroupsStatus, dispatch]);

    const handleSave = async () => {
        if (!instance) return;
        setSaving(true);
        try {
            const groupNames = selectedGroups.map(g => g.value);
            await axiosInstance.put('/v1/instance/security-groups', {
                instanceId: instance.id,
                securityGroups: groupNames
            });
            showToast("Security groups updated successfully", "success");
            onUpdated();
            onClose();
        } catch (error) {
            console.error("Failed to update security groups:", error);
            showToast("Failed to update security groups", "error");
        } finally {
            setSaving(false);
        }
    };

    const options = availableGroups.map(sg => ({value: sg.name, label: sg.name}));

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Change Security Groups for ${instance?.name}`} size="md">
            <div className="security-group-modal-body" style={{padding: '20px'}}>
                <p style={{marginBottom: '15px', color: 'var(--text-muted)'}}>
                    Select the security groups for this instance.
                </p>

                {securityGroupsStatus === 'loading' ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>Loading available groups...</div>
                ) : (
                    <div style={{marginBottom: '25px'}}>
                        <label style={{display: 'block', marginBottom: '8px', fontWeight: 500}}>
                            Security Groups
                        </label>
                        <Select
                            isMulti
                            options={options}
                            value={selectedGroups}
                            onChange={(selected) => setSelectedGroups(selected as any)}
                            placeholder="Select security groups..."
                            classNamePrefix="react-select"
                        />
                    </div>
                )}

                <div className="modal-actions"
                     style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px'}}>
                    <button
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={saving}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-card)',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving || securityGroupsStatus === 'loading'}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'var(--action-primary-bg)',
                            color: 'var(--action-primary-text)',
                            cursor: (saving || securityGroupsStatus === 'loading') ? 'not-allowed' : 'pointer',
                            opacity: (saving || securityGroupsStatus === 'loading') ? 0.7 : 1
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SecurityGroupModal;
